import { type ReactNode, useEffect, useRef } from "react";
import { AnimatePresence, isWeb, type TamaguiElement, usePresence } from "tamagui";
import { YStack } from "../Stacks";
import { presenceTransition } from "./motion";
const SLIDE_OFFSET = 48;
export type SlideTransitionProps = {
  stateKey: string;
  /** +1 forward, -1 back. */
  direction: 1 | -1;
  children: ReactNode;
};

// The present screen sits in normal flow so an auto-height page layout grows
// with it; only a leaving screen goes absolute, over the arriving one.
const Screen = ({
  direction,
  children
}: Omit<SlideTransitionProps, "stateKey">) => {
  const [isPresent] = usePresence();
  // A leaving screen sits over the arriving one until its exit ends; keep it
  // out of the tap and Tab order for that frame or two.
  const ref = useRef<TamaguiElement>(null);
  useEffect(() => {
    if (isWeb && ref.current instanceof HTMLElement) {
      ref.current.toggleAttribute("inert", !isPresent);
    }
  }, [isPresent]);
  return <YStack ref={ref} pointerEvents={isPresent ? "auto" : "none"} position={isPresent ? "relative" : "absolute"} flex={isPresent ? 1 : undefined} top={0} left={0} right={0} bottom={0} width="100%" transition={presenceTransition} animateOnly={["opacity", "transform"]} opacity={1} enterStyle={{
    opacity: 0,
    x: direction * SLIDE_OFFSET
  }} exitStyle={{
    opacity: 0,
    x: -direction * SLIDE_OFFSET
  }} data-sentry-element="YStack" data-sentry-component="Screen" data-sentry-source-file="SlideTransition.tsx">
      {children}
    </YStack>;
};

/** Keyed screen swap: the leaving screen slides out over the arriving one. */
export const SlideTransition = ({
  stateKey,
  direction,
  children
}: SlideTransitionProps) => <AnimatePresence initial={false} data-sentry-element="AnimatePresence" data-sentry-component="SlideTransition" data-sentry-source-file="SlideTransition.tsx">
    <Screen key={stateKey} direction={direction} data-sentry-element="Screen" data-sentry-source-file="SlideTransition.tsx">
      {children}
    </Screen>
  </AnimatePresence>;